import { useMemo, useState } from 'react';
import { type Tab } from '@swtorbit/shared-components';
import { Modal, Tabs } from '@swtorbit/ui/react';
import {
  extractCoordsFromLogLine,
  DEFAULT_TRL_STALEMATE_CONFIG,
  trlStalemateConfigEquals,
  DEFAULT_TRL_RADAR_CONFIG,
  trlRadarConfigEquals,
  describeTrlTreasureItem,
  asTrlDifficulty,
  TRL_DIFFICULTY_EMOJI,
  TRL_TREASURE_TIER_LABELS,
  isTrlSabotageEligibleNode,
  type TrlTreasureConfig,
  type TrlImageAsset,
  type TrlMapNodeDef,
  type TrlRoundType,
  type TrlStalemateConfig,
  type TrlRadarConfig,
} from '@swtorbit/shared-socket-events';
import { useTrlImageLibrary } from '../../content-library';
import { StalemateConfigFields } from './StalemateConfigFields';
import { RadarConfigFields } from './RadarConfigFields';
import { GalleryNavbar } from '../../content-library/trailblazers/GalleryNavbar';
import { TRL_MAP_ICONS, getMapIconUrl } from '@swtorbit/shared-assets';
import type { CSSProperties } from 'react';
import {
  useGalleryFilters,
  EMPTY_FILTERS,
  DEFAULT_SORT,
  type GalleryFilters,
  type GallerySort,
} from '../../content-library/trailblazers/useGalleryFilters';
import { API_URL } from '../../../config';

/**
 * Everything the planet editor can change about a node.
 *
 * A single patch object rather than a positional argument list: the editor now
 * owns eight-plus independent settings, and an ordered signature that long is
 * easy to mis-wire silently at the call site.
 */
export interface TrlNodeConfigPatch {
  imageIds: string[];
  isCheckpoint: boolean;
  roundType: TrlRoundType;
  triangulationTarget: { x: number; y: number; z: number } | undefined;
  stalemateOverride: TrlStalemateConfig | null;
  radarOverride: TrlRadarConfig | null;
  icon: string | null;
  iconColor: string | null;
  /** Treasure item ids on this planet; empty = none. */
  treasureItemIds: string[];
  /** Library image id of the Con Clue fake; null = none. */
  decoyImageId: string | null;
}

/** Which selection a gallery tile click edits. */
type GallerySelectMode = 'real' | 'decoy';

interface PlanetEditModalProps {
  node: TrlMapNodeDef;
  /**
   * View-only: the map is locked (the game has left draft). The modal is still
   * how an admin READS a planet — the dots carry no markers or hover card — so
   * it opens with every control disabled and nothing to save.
   */
  readOnly?: boolean;
  /** Game-level stalemate defaults, used to seed a new per-node override. */
  gameStalemateConfig?: TrlStalemateConfig | null;
  /** Game-level radar defaults, used to seed a new per-node override. */
  gameRadarConfig?: TrlRadarConfig | null;
  /** The game's treasure pool, for the per-planet picker. */
  gameTreasureConfig?: TrlTreasureConfig | null;
  /** Con Clue sabotage is enabled: eligible planets need a fake image. */
  conClueEnabled?: boolean;
  onConfirm: (patch: TrlNodeConfigPatch) => void;
  onDelete: () => void;
  onCancel: () => void;
}

export function PlanetEditModal({ node, readOnly = false, gameStalemateConfig, gameRadarConfig, gameTreasureConfig, conClueEnabled = false, onConfirm, onDelete, onCancel }: PlanetEditModalProps) {
  const { images, imagesLoading } = useTrlImageLibrary();

  const [roundType, setRoundType] = useState<TrlRoundType>(node.roundType ?? 'single');
  const [selected, setSelected] = useState<string[]>(node.images.map((r) => r.imageId));

  // Con Clue fake. Start and final planets can never hold a sabotage, so they
  // never need one; the picker shows while Con Clue is on (or a fake is already
  // set, so it can be removed).
  const [decoyImageId, setDecoyImageId] = useState<string | null>(node.decoyImage?.imageId ?? null);
  const decoyEligible = isTrlSabotageEligibleNode(node);
  const showDecoyPicker = decoyEligible && (conClueEnabled || decoyImageId !== null);
  const [selectMode, setSelectMode] = useState<GallerySelectMode>('real');
  const pickingDecoy = showDecoyPicker && selectMode === 'decoy';
  const [isCheckpoint, setIsCheckpoint] = useState(node.isCheckpoint);
  const [triangulationTarget, setTriangulationTarget] = useState<{ x: number; y: number; z: number } | undefined>(
    node.triangulationTarget,
  );
  // The resolved game-level defaults this planet inherits from. The working copy
  // below is always seeded from any saved override, else these defaults, so the
  // admin sees the effective values and can edit them in place.
  const resolvedDefaults = gameStalemateConfig ?? DEFAULT_TRL_STALEMATE_CONFIG;
  const [stalemateConfig, setStalemateConfig] = useState<TrlStalemateConfig>(
    node.stalemateOverride ?? resolvedDefaults,
  );
  const resolvedRadarDefaults = gameRadarConfig ?? DEFAULT_TRL_RADAR_CONFIG;
  const [radarConfig, setRadarConfig] = useState<TrlRadarConfig>(
    node.radarOverride ?? resolvedRadarDefaults,
  );
  const [combatLog, setCombatLog] = useState('');

  // Custom node icon: shape id (null = default white dot) + tint colour.
  const [icon, setIcon] = useState<string | null>(node.icon ?? null);
  const [iconColor, setIconColor] = useState<string>(node.iconColor ?? '#ffffff');

  // Treasure sitting on this planet. Also editable in bulk from the Game
  // Settings → Treasure List tab; both routes write the same node field.
  const [treasureItemIds, setTreasureItemIds] = useState<string[]>(node.treasureItemIds ?? []);
  const treasurePool = gameTreasureConfig?.items ?? [];

  // Gallery filter/sort state (no localStorage persistence — this is a picker, not the full library)
  const [filters, setFilters] = useState<GalleryFilters>(EMPTY_FILTERS);
  const [sort, setSort] = useState<GallerySort>(DEFAULT_SORT);

  // Which settings tab the right-hand panel is showing.
  const [activeTab, setActiveTab] = useState<string>('planet');

  // ── Selection order map ─────────────────────────────────────────────────────

  const selectedOrder = useMemo(() => {
    const map = new Map<string, number>();
    selected.forEach((id, i) => map.set(id, i + 1));
    return map;
  }, [selected]);

  // ── Selected image metadata (for constraint checks) ─────────────────────────

  const selectedImages = useMemo(
    () => selected.map((id) => images.find((i) => i.id === id)).filter((i): i is TrlImageAsset => !!i),
    [selected, images],
  );

  // Planet lock: first selected image's area name (the platform area's name)
  const lockedArea = roundType === 'triangulation' && selected.length > 0
    ? selectedImages[0]?.area ?? null
    : null;

  // Faction constraint
  const hasRepublicOnly = selectedImages.some((i) => i.faction === 'republic');
  const hasImperialOnly = selectedImages.some((i) => i.faction === 'imperial');

  // ── Constraint checks for display ───────────────────────────────────────────

  const hasMixedPlanets = useMemo(() => {
    if (roundType !== 'triangulation' || selectedImages.length < 2) return false;
    const areas = new Set(selectedImages.map((i) => i.area));
    return areas.size > 1;
  }, [roundType, selectedImages]);

  const hasFactionConflict = hasRepublicOnly && hasImperialOnly;

  const decoyImage = decoyImageId ? images.find((i) => i.id === decoyImageId) : undefined;

  // ── Per-image disabled check ─────────────────────────────────────────────────

  const isDisabledForConstraints = (img: TrlImageAsset): boolean => {
    if (pickingDecoy) {
      if (img.id === decoyImageId) return false; // can always deselect
      if (selected.includes(img.id)) return true; // a real image can't be its own fake
      if (roundType === 'single') return false;
      // A triangulation fake must pass for the other two.
      if (lockedArea && img.area !== lockedArea) return true;
      if (hasRepublicOnly && img.faction === 'imperial') return true;
      if (hasImperialOnly && img.faction === 'republic') return true;
      return false;
    }
    if (img.id === decoyImageId) return true; // already this planet's fake
    if (selected.includes(img.id)) return false; // can always deselect
    if (roundType === 'single') return false;
    if (selected.length >= 3) return true; // cap reached
    if (lockedArea && img.area !== lockedArea) return true; // wrong planet
    if (hasRepublicOnly && img.faction === 'imperial') return true;
    if (hasImperialOnly && img.faction === 'republic') return true;
    return false;
  };

  // ── Filter: lock area when triangulation planet is chosen ────────────────────

  const effectiveFilters = useMemo((): GalleryFilters => {
    if (roundType === 'triangulation' && lockedArea) {
      return { ...filters, area: lockedArea };
    }
    return filters;
  }, [filters, roundType, lockedArea]);

  const visibleImages = useGalleryFilters(images, effectiveFilters, sort);

  // ── Toggle selection ─────────────────────────────────────────────────────────

  const toggle = (id: string) => {
    if (pickingDecoy) {
      setDecoyImageId((prev) => (prev === id ? null : id));
      return;
    }
    if (roundType === 'single') {
      setSelected((prev) => (prev.includes(id) ? [] : [id]));
    } else {
      setSelected((prev) => {
        if (prev.includes(id)) return prev.filter((x) => x !== id);
        if (prev.length >= 3) return prev;
        return [...prev, id];
      });
    }
  };

  // ── Round type switch ────────────────────────────────────────────────────────

  const switchRoundType = (rt: TrlRoundType) => {
    setRoundType(rt);
    setSelected([]); // clear on type change — admin must re-select
    setDecoyImageId(null); // its constraints depend on the round type too
    setTriangulationTarget(undefined);
  };

  // ── Combat log → target coords ───────────────────────────────────────────────

  const handleCombatLogChange = (value: string) => {
    setCombatLog(value);
    const parsed = extractCoordsFromLogLine(value);
    if (parsed) {
      setTriangulationTarget({ x: parsed.x, y: parsed.y, z: parsed.z });
    }
  };

  const updateTargetAxis = (axis: 'x' | 'y' | 'z', raw: string) => {
    const v = parseFloat(raw);
    setTriangulationTarget((prev) => ({
      x: prev?.x ?? 0,
      y: prev?.y ?? 0,
      z: prev?.z ?? 0,
      [axis]: isNaN(v) ? 0 : v,
    }));
  };

  // ── Can save? ────────────────────────────────────────────────────────────────

  const canSave =
    roundType === 'single'
      ? selected.length === 1
      : selected.length === 3 && !hasMixedPlanets && !hasFactionConflict && triangulationTarget !== undefined;

  // True when the working copy still matches the game defaults — then this planet
  // stores no override (inherits, and tracks future default changes).
  const inheritsDefaults = trlStalemateConfigEquals(stalemateConfig, resolvedDefaults);
  const inheritsRadarDefaults = trlRadarConfigEquals(radarConfig, resolvedRadarDefaults);

  const handleConfirm = () => {
    if (!canSave) return;
    const stalemateOverride = inheritsDefaults ? null : stalemateConfig;
    const radarOverride = inheritsRadarDefaults ? null : radarConfig;
    onConfirm({
      imageIds: selected,
      isCheckpoint,
      roundType,
      triangulationTarget,
      stalemateOverride,
      radarOverride,
      icon,
      // Colour is only meaningful when a shape is chosen; store null otherwise.
      iconColor: icon ? iconColor : null,
      treasureItemIds,
      // A start/final planet never keeps a fake (the server drops it too).
      decoyImageId: decoyEligible ? decoyImageId : null,
    });
  };

  /**
   * Everything currently blocking Save. Rendered outside the tab panel so a
   * disabled Save button is always explained, whichever tab is open.
   */
  const blockingWarnings: Array<{ text: string; severity: 'warning' | 'error' }> = [];
  if (roundType === 'single' && selected.length !== 1) {
    blockingWarnings.push({ text: `Select exactly 1 image (${selected.length}/1)`, severity: 'warning' });
  }
  if (roundType === 'triangulation' && selected.length !== 3) {
    blockingWarnings.push({ text: `Select exactly 3 images (${selected.length}/3)`, severity: 'warning' });
  }
  if (roundType === 'triangulation' && triangulationTarget === undefined) {
    blockingWarnings.push({ text: 'Target coordinates required', severity: 'warning' });
  }
  if (hasMixedPlanets) {
    blockingWarnings.push({ text: 'All images must be from the same planet', severity: 'error' });
  }
  if (hasFactionConflict) {
    blockingWarnings.push({ text: 'Cannot mix Republic-only and Imperial-only images', severity: 'error' });
  }

  // Not blocking: the map's validation gates the lobby, and start/final status can
  // change with the connections after this planet is saved.
  const decoyWarnings: string[] = [];
  if (showDecoyPicker && conClueEnabled && !decoyImageId) {
    decoyWarnings.push('Con Clue is enabled — this planet needs a fake image');
  }
  if (decoyImageId && selected.includes(decoyImageId)) {
    decoyWarnings.push('The fake image cannot be one of the real images');
  }
  if (decoyImage && roundType === 'triangulation') {
    if (lockedArea && decoyImage.area !== lockedArea) {
      decoyWarnings.push('A triangulation fake must be from the same planet as the real images');
    }
    if ((hasRepublicOnly && decoyImage.faction === 'imperial') || (hasImperialOnly && decoyImage.faction === 'republic')) {
      decoyWarnings.push("The fake's faction conflicts with the real images");
    }
  }

  // ── Render ───────────────────────────────────────────────────────────────────

  const renderTabContent = () => {
    switch (activeTab) {
      // Core planet identity: how it behaves and which images it uses.
      case 'planet':
        return (
          <div className="d-flex flex-column gap-sm">
            {/* Checkpoint toggle — applies to the planet's whole round on confirm */}
            <div className="d-flex align-items-center gap-xs flex-wrap">
              <span className="fs-sm">Checkpoint:</span>
              <button
                type="button"
                className={`btn btn--sm ${isCheckpoint ? 'btn--amber' : 'btn--ghost'}`}
                onClick={() => setIsCheckpoint((v) => !v)}
                title="A checkpoint holds teams until the field catches up"
              >
                {isCheckpoint ? '✓ Checkpoint active' : 'Set as Checkpoint'}
              </button>
              {isCheckpoint !== node.isCheckpoint && (
                <span className="fs-xs text-slate">
                  {node.derivedLevel
                    ? `Applies to every planet in round ${node.derivedLevel}.`
                    : 'Applies to every planet in this round.'}
                </span>
              )}
            </div>

            {/* Node marker: replace the default white dot with a piece of SVG
                artwork. None keeps the classic dot. Swatches show each marker as
                it will actually render (as-authored, never recoloured), and the
                colour picker tints the marker's pulsing halo rather than its fill.
                The list is a scrolling grid so it stays a fixed size as the marker
                set grows. */}
            <div className="d-flex flex-column gap-2xs">
              <strong className="fs-sm">Node marker</strong>
              <div
                className="trl-planet-edit-modal__marker-grid d-grid gap-2xs p-2xs rounded-xs border border-teal-dark overflow-auto"
              >
                <button
                  type="button"
                  className={`btn btn--sm ${icon === null ? '' : 'btn--ghost'}`}
                  onClick={() => setIcon(null)}
                  title="Default white dot"
                >
                  Dot
                </button>
                {TRL_MAP_ICONS.map((ic) => {
                  // Drawn, not masked — the swatch must preview the real artwork.
                  const swatchStyle = {
                    display: 'inline-block',
                    width: '1.75rem',
                    height: '1.75rem',
                    backgroundImage: `url("${getMapIconUrl(ic.id)}")`,
                    backgroundSize: 'contain',
                    backgroundRepeat: 'no-repeat',
                    backgroundPosition: 'center',
                  } as CSSProperties;
                  return (
                    <button
                      key={ic.id}
                      type="button"
                      className={`btn btn--sm d-flex align-items-center justify-content-center ${icon === ic.id ? '' : 'btn--ghost'}`}
                      onClick={() => setIcon(ic.id)}
                      title={ic.label}
                      style={{ padding: '0.35rem' }}
                    >
                      <span style={swatchStyle} />
                    </button>
                  );
                })}
              </div>
              {icon && (
                <label className="d-flex align-items-center gap-xs fs-sm mt-2xs">
                  <span title="Tints the marker's pulsing halo — the artwork keeps its own colours">
                    Glow colour
                  </span>
                  <input
                    type="color"
                    className="input"
                    style={{ width: '4rem', padding: 0 }}
                    value={toHexColor(iconColor)}
                    onChange={(e) => setIconColor(e.target.value)}
                  />
                </label>
              )}
            </div>

            {/* Round type radio */}
            <div className="d-flex flex-column gap-2xs">
              <strong className="fs-sm">Round type</strong>
              {(['single', 'triangulation'] as TrlRoundType[]).map((rt) => (
                <label key={rt} className="d-flex align-items-center gap-2xs fs-sm">
                  <input
                    className="radio"
                    type="radio"
                    name="trl-round-type"
                    value={rt}
                    checked={roundType === rt}
                    onChange={() => switchRoundType(rt)}
                  />
                  {rt === 'single' ? (
                    <span>
                      <strong>Single</strong>
                      <span className="fs-xs text-slate d-block">1 image — first dig wins</span>
                    </span>
                  ) : (
                    <span>
                      <strong>Triangulation</strong>
                      <span className="fs-xs text-slate d-block">3 clues → reveal 4th target</span>
                    </span>
                  )}
                </label>
              ))}
            </div>

            {/* Selection summary */}
            <div className="d-flex flex-column gap-2xs">
              <strong className="fs-sm">
                Selected images ({selected.length}{roundType === 'triangulation' ? '/3' : '/1'})
              </strong>
              {selected.length === 0 ? (
                <p className="text-slate fs-xs">
                  {roundType === 'single' ? 'Select 1 image from the gallery.' : 'Select 3 images from the same planet.'}
                </p>
              ) : (
                <ol className="d-flex flex-column gap-2xs ps-sm">
                  {selected.map((id) => {
                    const img = images.find((i) => i.id === id);
                    return (
                      <li key={id} className="fs-xs">
                        <span className="text-white">{img?.name ?? id}</span>
                        {img && <span className="text-slate"> — {img.area}</span>}
                        {img?.faction && img.faction !== 'both' && (
                          <span className={`badge badge--sm ms-2xs ${img.faction === 'republic' ? 'badge--blue' : 'badge--red'}`}>
                            {img.faction}
                          </span>
                        )}
                        <button
                          type="button"
                          className="btn btn--xs btn--ghost ms-2xs text-red"
                          onClick={() => toggle(id)}
                          title="Remove"
                        >
                          ×
                        </button>
                      </li>
                    );
                  })}
                </ol>
              )}
            </div>

            {/* Con Clue fake */}
            {showDecoyPicker && (
              <div className="d-flex flex-column gap-2xs">
                <strong className="fs-sm">Con Clue fake image</strong>
                <p className="field__hint fs-xs">
                  {roundType === 'triangulation'
                    ? 'Shown to a rival team in place of one of the 3 images while a Con Clue fools them. Must be from the same planet.'
                    : 'Shown to a rival team in place of the real image while a Con Clue fools them.'}{' '}
                  Pick it with “Con Clue fake” above the gallery.
                </p>
                {decoyImageId ? (
                  <span className="fs-xs">
                    <span className="text-white">{decoyImage?.name ?? decoyImageId}</span>
                    {decoyImage && <span className="text-slate"> — {decoyImage.area}</span>}
                    <button
                      type="button"
                      className="btn btn--xs btn--ghost ms-2xs text-red"
                      onClick={() => setDecoyImageId(null)}
                      title="Remove"
                    >
                      ×
                    </button>
                  </span>
                ) : (
                  <span className="text-slate fs-xs">No fake image selected.</span>
                )}
              </div>
            )}
            {!decoyEligible && conClueEnabled && (
              <p className="field__hint fs-xs">
                Starting and final planets can&apos;t hold a sabotage, so no Con Clue fake is needed.
              </p>
            )}

            {/* Triangulation target */}
            {roundType === 'triangulation' && (
              <div className="d-flex flex-column gap-2xs">
                <strong className="fs-sm">Triangulation target coordinates</strong>
                <p className="field__hint fs-xs">
                  The secret 4th bury location revealed after all 3 clues are activated. Never shown to players.
                </p>
                <div className="d-flex gap-xs align-items-center flex-wrap">
                  {(['x', 'y', 'z'] as const).map((axis) => (
                    <label key={axis} className="d-flex align-items-center gap-2xs fs-sm">
                      {axis.toUpperCase()}
                      <input
                        type="number"
                        className="input"
                        style={{ width: '7rem' }}
                        step="0.001"
                        value={triangulationTarget?.[axis] ?? ''}
                        onChange={(e) => updateTargetAxis(axis, e.target.value)}
                        placeholder="0"
                      />
                    </label>
                  ))}
                </div>
                <details className="mt-2xs">
                  <summary className="fs-xs text-slate-dark cursor-pointer">Fill from combat log</summary>
                  <div className="mt-2xs">
                    <textarea
                      className="input fs-xs"
                      rows={3}
                      placeholder="Paste a combat log line with coordinates…"
                      value={combatLog}
                      onChange={(e) => handleCombatLogChange(e.target.value)}
                    />
                    <p className="field__hint fs-xs">
                      Paste a line containing coordinates from the game&apos;s combat log. X, Y, Z will be auto-filled.
                    </p>
                  </div>
                </details>
              </div>
            )}
          </div>
        );

      // Per-node stalemate + planet-name settings. Preloaded from the game
      // defaults; edit in place to override, or Reset to inherit again.
      case 'stalemate':
        return (
          <div className="d-flex flex-column gap-2xs">
            <div className="d-flex align-items-center justify-content-between gap-xs">
              <strong className="fs-sm">Stalemate &amp; planet-name settings</strong>
              <button
                type="button"
                className="btn btn--outline btn--slate btn--sm"
                onClick={() => setStalemateConfig(resolvedDefaults)}
                disabled={inheritsDefaults}
                title="Reset these to the game-level defaults"
              >
                Reset
              </button>
            </div>
            <span className="fs-xs text-slate">
              {inheritsDefaults
                ? 'Matches the game defaults — this planet inherits future changes to them.'
                : 'Overriding the game defaults for this planet only.'}
            </span>
            <div className="mt-2xs">
              <StalemateConfigFields value={stalemateConfig} onChange={setStalemateConfig} />
            </div>
          </div>
        );

      // Per-node radar-range settings, same inherit/override model as above.
      case 'radar':
        return (
          <div className="d-flex flex-column gap-2xs">
            <div className="d-flex align-items-center justify-content-between gap-xs">
              <strong className="fs-sm">Radar ranges</strong>
              <button
                type="button"
                className="btn btn--outline btn--slate btn--sm"
                onClick={() => setRadarConfig(resolvedRadarDefaults)}
                disabled={inheritsRadarDefaults}
                title="Reset these to the game-level defaults"
              >
                Reset
              </button>
            </div>
            <span className="fs-xs text-slate">
              {inheritsRadarDefaults
                ? 'Matches the game defaults — this planet inherits future changes to them.'
                : 'Overriding the game defaults for this planet only.'}
            </span>
            <div className="mt-2xs">
              <RadarConfigFields value={radarConfig} onChange={setRadarConfig} />
            </div>
          </div>
        );

      // Treasure on this planet. The pool itself lives in Game Settings →
      // Treasure List, which is also where a whole-map re-roll happens; this is
      // the per-planet adjustment.
      case 'treasure':
        return (
          <div className="d-flex flex-column gap-2xs">
            <strong className="fs-sm">Treasure</strong>
            {treasurePool.length === 0 ? (
              <span className="fs-xs text-slate">
                No treasure defined for this game yet — add rewards in Game Settings → Treasure List.
              </span>
            ) : (
              <>
                <span className="fs-xs text-slate">
                  Won by the first team to clear this planet.
                </span>
                {treasureItemIds.length === 0 ? (
                  <span className="fs-xs text-slate">No treasure on this planet.</span>
                ) : (
                  treasureItemIds.map((itemId, index) => {
                    const item = treasurePool.find((i) => i.id === itemId);
                    return (
                      <div
                        key={`${itemId}-${index}`}
                        className="d-flex align-items-center justify-content-between gap-xs"
                      >
                        <span className="fs-xs">
                          {item ? (
                            <>
                              💎 {describeTrlTreasureItem(item)}
                              <span className="text-slate"> · {TRL_TREASURE_TIER_LABELS[item.tier]}</span>
                            </>
                          ) : (
                            <span className="text-amber">Removed reward</span>
                          )}
                        </span>
                        <button
                          type="button"
                          className="btn btn--outline btn--slate btn--sm"
                          onClick={() =>
                            setTreasureItemIds((prev) => prev.filter((_, i) => i !== index))
                          }
                          aria-label="Remove this treasure"
                        >
                          ×
                        </button>
                      </div>
                    );
                  })
                )}
                <select
                  className="input input--sm"
                  value=""
                  onChange={(e) => {
                    const id = e.target.value;
                    if (id) setTreasureItemIds((prev) => [...prev, id]);
                    e.target.value = '';
                  }}
                  aria-label="Add treasure to this planet"
                >
                  <option value="">+ Add treasure…</option>
                  {treasurePool.map((it) => (
                    <option key={it.id} value={it.id}>
                      {describeTrlTreasureItem(it)} ({TRL_TREASURE_TIER_LABELS[it.tier]})
                    </option>
                  ))}
                </select>
              </>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <Modal open onClose={onCancel} className="round-creation-modal trl-planet-edit-modal trl-planet-edit-modal--wide" title={<span className="d-flex align-items-center gap-xs flex-wrap">
          <span>{node.label}</span>
          <span className="badge">{node.derivedLevel ? `Round ${node.derivedLevel}` : 'Unplaced'}</span>
          {node.isStart && <span className="text-green-dark fs-sm">● Start</span>}
          {node.isFinal && <span className="text-gold fs-sm">★ Final</span>}
        </span>} footer={
<>
        {readOnly ? (
          <button type="button" className="btn btn--outline btn--slate" style={{ marginLeft: 'auto' }} onClick={onCancel}>
            Close
          </button>
        ) : (
          <>
            <button type="button" className="btn btn--red" onClick={onDelete}>
              Delete Planet
            </button>
            <div className="d-flex gap-xs" style={{ marginLeft: 'auto' }}>
              <button type="button" className="btn btn--outline btn--slate" onClick={onCancel}>
                Cancel
              </button>
              <button
                type="button"
                className="btn btn--green"
                disabled={!canSave}
                onClick={handleConfirm}
                title={!canSave ? 'Fix the warnings above before saving' : undefined}
              >
                Save
              </button>
            </div>
          </>
        )}
      </>
}>
        {/* One disabled fieldset turns the whole editor view-only: it disables
            every nested control natively, so no individual input has to know
            about readOnly. `d-contents` keeps it out of the layout, so the
            grid below still stretches inside the flex body. */}
        <fieldset disabled={readOnly} className="d-contents">
        <div className="trl-planet-edit-modal__layout">
          {/* ── Left: image gallery ─────────────────────────────────── */}
          <div className="trl-planet-edit-modal__gallery-panel">
            <GalleryNavbar
              images={images}
              filters={effectiveFilters}
              onFiltersChange={(next) => {
                // If triangulation planet is locked, ignore area changes from the navbar
                if (roundType === 'triangulation' && lockedArea) {
                  setFilters({ ...next, area: lockedArea });
                } else {
                  setFilters(next);
                }
              }}
              sort={sort}
              onSortChange={setSort}
              resultCount={visibleImages.length}
              // Omit onAdd/onRefresh/onStats — admin actions not needed in picker context
            />

            {showDecoyPicker && (
              <div className="d-flex align-items-center gap-2xs mt-2xs flex-wrap">
                <span className="fs-sm">Selecting:</span>
                <button
                  type="button"
                  className={`btn btn--sm ${selectMode === 'real' ? '' : 'btn--ghost'}`}
                  onClick={() => setSelectMode('real')}
                >
                  Real images
                </button>
                <button
                  type="button"
                  className={`btn btn--sm ${selectMode === 'decoy' ? 'btn--amber' : 'btn--ghost'}`}
                  onClick={() => setSelectMode('decoy')}
                >
                  Con Clue fake
                </button>
              </div>
            )}

            {roundType === 'triangulation' && lockedArea && (
              <div className="d-flex align-items-center gap-2xs mt-2xs">
                <span className="badge badge--blue">Planet locked: {lockedArea}</span>
                <span className="fs-xs text-slate">
                  {pickingDecoy ? 'The fake must be from this planet too' : 'All 3 images must be from this planet'}
                </span>
              </div>
            )}

            {/* Only the tile grid scrolls — the filter navbar above stays put,
                and the modal's footer stays pinned regardless of library size. */}
            <div className="trl-planet-edit-modal__gallery-scroll">
              {imagesLoading ? (
                <p className="text-slate mt-xs">Loading library…</p>
              ) : images.length === 0 ? (
                <p className="text-amber mt-xs">
                  The Content Library has no Trailblazers images yet — add some in the Content Library tab.
                </p>
              ) : visibleImages.length === 0 ? (
                <p className="text-slate mt-xs">No screenshots match your filters.</p>
              ) : (
                <div className="gallery mt-xs">
                  {visibleImages.map((img) => {
                    const order = selectedOrder.get(img.id);
                    const isDecoy = img.id === decoyImageId;
                    const isImg = order !== undefined || isDecoy;
                    const isDisabled = isDisabledForConstraints(img);
                    const originalSrc = `${API_URL}${img.url}`;
                    const thumbSrc = img.thumbUrl ? `${API_URL}${img.thumbUrl}` : originalSrc;
                    return (
                      <button
                        key={img.id}
                        type="button"
                        className={`gallery-tile${isImg ? ' gallery-tile--selected' : ''}${isDisabled ? ' gallery-tile--disabled' : ''}`}
                        onClick={() => !isDisabled && toggle(img.id)}
                        disabled={isDisabled}
                        title={isDisabled ? 'Not compatible with current selection' : img.name}
                      >
                        <img
                          className="gallery-tile__img"
                          src={thumbSrc}
                          alt={img.name}
                          loading="lazy"
                          decoding="async"
                          onError={(e) => { if ((e.target as HTMLImageElement).src !== originalSrc) (e.target as HTMLImageElement).src = originalSrc; }}
                        />
                        <div className="gallery-tile__caption">
                          <span className="gallery-tile__title">{img.name}</span>
                          <span className="gallery-tile__subtitle">
                            {(() => {
                              const d = asTrlDifficulty(img.difficulty);
                              return d ? TRL_DIFFICULTY_EMOJI[d] : '📍';
                            })()} {img.area}
                          </span>
                        </div>
                        {order !== undefined && (
                          <span className="gallery-tile__badge gallery-tile__badge--order">{order}</span>
                        )}
                        {isDecoy && (
                          <span className="gallery-tile__badge gallery-tile__badge--order" title="Con Clue fake">F</span>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* ── Right: tabbed settings ──────────────────────────────── */}
          <div className="trl-planet-edit-modal__controls-panel">
            <Tabs variant="bordered" items={PLANET_TABS.map((t) => ({ value: t.id, label: t.label }))} value={activeTab} onChange={setActiveTab}>{renderTabContent()}</Tabs>

            {/* Blocking warnings live outside the tab panel: a Save that can't
                fire should never be explained on a tab you aren't looking at. */}
            {blockingWarnings.length > 0 && (
              <div className="trl-planet-edit-modal__warnings d-flex flex-column gap-2xs">
                {blockingWarnings.map((w) => (
                  <span key={w.text} className={`fs-sm ${w.severity === 'error' ? 'text-red' : 'text-amber'}`}>
                    {w.text}
                  </span>
                ))}
              </div>
            )}
            {decoyWarnings.length > 0 && (
              <div className="trl-planet-edit-modal__warnings d-flex flex-column gap-2xs">
                {decoyWarnings.map((text) => (
                  <span key={text} className="fs-sm text-amber">
                    {text}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
        </fieldset>
      </Modal>
  );
}

/** The right-hand settings panel, split by concern. */
const PLANET_TABS: Tab[] = [
  { id: 'planet', label: 'Planet' },
  { id: 'stalemate', label: 'Stalemate' },
  { id: 'radar', label: 'Radar' },
  { id: 'treasure', label: 'Treasure' },
];


/**
 * `<input type="color">` only accepts `#rrggbb`. Fall back to white for any
 * other CSS colour form so the swatch stays usable; picking overwrites cleanly.
 */
function toHexColor(color: string): string {
  return /^#[0-9a-fA-F]{6}$/.test(color) ? color : '#ffffff';
}
